/**
 * API Configuration
 *
 * Centralizes API endpoint configuration based on environment.
 * Uses a unified domain structure: {ENV}.knowblemind.com
 *
 * Endpoints:
 * - Prompt Generation: /prompt/*
 * - Quiz CRUD: /quiz/*
 * - Subscriptions: /subscriptions/*
 * - Analytics: /analytics/*
 * - Admin: /admin/*
 */

type Environment = 'development' | 'staging' | 'production';

/**
 * Get the current environment from Vite environment variable
 */
function getEnvironment(): Environment {
  const env = import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE;

  if (env === 'production' || env === 'prod') {
    return 'production';
  }

  if (env === 'staging' || env === 'stage') {
    return 'staging';
  }

  return 'development';
}

/**
 * Get the base domain for the current environment
 * - production: knowblemind.com
 * - staging: stage.knowblemind.com
 * - development: dev.knowblemind.com
 */
function getBaseDomain(): string {
  const environment = getEnvironment();

  switch (environment) {
    case 'production':
      return 'api.knowblemind.com';
    case 'staging':
      return 'api-stage.knowblemind.com';
    case 'development':
      return 'api-dev.knowblemind.com';
    default:
      return 'api-dev.knowblemind.com';
  }
}

/**
 * Get the base API URL for the current environment
 */
function getBaseUrl(): string {
  const baseDomain = getBaseDomain();
  return `https://${baseDomain}`;
}

/**
 * API endpoint paths
 */
export const API_PATHS = {
  // Prompt Generation endpoints
  PROMPT: {
    BASE: '/prompt',
    GENERATE_QUIZ: '/prompt/generate-quiz',
    GET_STATUS: (jobId: string) => `/prompt/get-quiz-status/${jobId}`,
    PENDING_QUIZZES: '/prompt/pending-quizzes',
    SAVE_PENDING_QUIZ: (id: string) => `/prompt/pending-quizzes/${id}/save`,
    DELETE_PENDING_QUIZ: (id: string) => `/prompt/pending-quizzes/${id}`,
  },

  // Quiz CRUD endpoints
  QUIZ: {
    BASE: '/quiz',
    QUIZLETS: '/quiz/quizlets',
    QUIZLET_BY_ID: (id: string) => `/quiz/quizlets/${id}`,
    DAILY_KNOWLEDGE: '/quiz/daily-knowledge',
    // Question management endpoints
    ADD_QUESTION: (quizletId: string) => `/quiz/quizlets/${quizletId}/questions`,
    UPDATE_QUESTION: (quizletId: string, questionId: string) => `/quiz/quizlets/${quizletId}/questions/${questionId}`,
    DELETE_QUESTION: (quizletId: string, questionId: string) => `/quiz/quizlets/${quizletId}/questions/${questionId}`,
    REORDER_QUESTIONS: (quizletId: string) => `/quiz/quizlets/${quizletId}/questions/reorder`,
    // Session tracking endpoints
    CREATE_SESSION: '/quiz/sessions',
    RECORD_RESPONSE: (sessionId: string) => `/quiz/sessions/${sessionId}/responses`,
    COMPLETE_SESSION: (sessionId: string) => `/quiz/sessions/${sessionId}/complete`,
    // Deck groups endpoints
    DECK_GROUPS: '/quiz/deck-groups',
    DECK_GROUP_BY_ID: (id: string) => `/quiz/deck-groups/${id}`,
    DECK_GROUP_START: (id: string) => `/quiz/deck-groups/${id}/start`,
  },

  // Subscription endpoints
  SUBSCRIPTIONS: {
    BASE: '/sub',
    CHECKOUT: '/sub/subscriptions/checkout',
    PORTAL: '/sub/subscriptions/portal',
    ME: '/sub/subscriptions/me',
    PROFILE_ME: '/sub/profile/me',
    PROFILE_CREATE: '/sub/profile/create',  // Create user profile for Firebase users
    USAGE_CHECK: '/sub/usage/check',
    QUIZ_COUNT: '/sub/quiz-count',
  },

  // Analytics endpoints
  ANALYTICS: {
    BASE: '/a',
    GLOBAL: '/a/global',
    QUIZLET: (id: string) => `/a/quizlet/${id}`,
    DAILY_KNOWLEDGE: '/a/daily-knowledge',
    QUESTION: (id: string) => `/a/question/${id}`,
    STREAK: '/a/streak',
    INSIGHTS: '/a/insights',
    PREFERENCES: '/a/preferences',
  },

  // Admin endpoints
  ADMIN: {
    BASE: '/p/admin',
    USERS: '/p/admin/users',
    USER_DETAILS: (userId: string) => `/p/admin/users/${userId}`,
    UPDATE_TIER: (userId: string) => `/p/admin/users/${userId}/tier`,
    UPDATE_PERMISSIONS: (userId: string) => `/p/admin/users/${userId}/permissions`,
    USER_ACTIVITY: (userId: string) => `/p/admin/users/${userId}/activity`,
  },

  // Articles endpoints
  ARTICLES: {
    BASE: '/articles',
    ARTICLES: '/articles/articles',
    ARTICLE_BY_ID: (id: string) => `/articles/articles/${id}`,
    PUBLIC: '/articles/articles/public',
    TOPICS: '/articles/articles/topics',
    SHARE: (articleId: string) => `/articles/articles/${articleId}/share`,
    REMOVE_SHARE: (articleId: string, userId: string) => `/articles/articles/${articleId}/share/${userId}`,
    LIST_SHARES: (articleId: string) => `/articles/articles/${articleId}/shares`,
    RECORD_VIEW: (articleId: string) => `/articles/articles/${articleId}/view`,
    COMMENTS: (articleId: string) => `/articles/articles/${articleId}/comments`,
    COMMENT_BY_ID: (articleId: string, commentId: string) => `/articles/articles/${articleId}/comments/${commentId}`,
    ANALYTICS: '/articles/a/articles',
    // Audio recordings
    RECORDINGS: (articleId: string) => `/articles/articles/${articleId}/recordings`,
    RECORDING_UPLOAD_URL: (articleId: string) => `/articles/articles/${articleId}/recordings/upload-url`,
    RECORDING_BY_ID: (articleId: string, recordingId: string) => `/articles/articles/${articleId}/recordings/${recordingId}`,
    RECORDING_PLAYBACK_URL: (articleId: string, recordingId: string) => `/articles/articles/${articleId}/recordings/${recordingId}/playback-url`,
    // Collections
    COLLECTIONS: '/articles/collections',
    COLLECTION_BY_ID: (id: string) => `/articles/collections/${id}`,
    COLLECTION_TREE: (id: string) => `/articles/collections/${id}/tree`,
    // Collection items (articles and headings)
    COLLECTION_ITEMS: (collectionId: string) => `/articles/collections/${collectionId}/items`,
    COLLECTION_ITEM_BY_ID: (collectionId: string, itemId: string) => `/articles/collections/${collectionId}/items/${itemId}`,
    COLLECTION_ITEMS_REORDER: (collectionId: string) => `/articles/collections/${collectionId}/items/reorder`,
    COLLECTION_ITEMS_BULK_ADD: (collectionId: string) => `/articles/collections/${collectionId}/items/bulk-add`,
  },
} as const;

/**
 * API Configuration object
 */
export const apiConfig = {
  /**
   * Get the base URL for the API
   */
  getBaseUrl,

  /**
   * Get the current environment
   */
  getEnvironment,

  /**
   * Get the base domain
   */
  getBaseDomain,

  /**
   * Full API URLs
   */
  urls: {
    /**
     * Prompt generation API base URL
     * Example: https://dev.knowblemind.com/prompt
     */
    get prompt() {
      return `${getBaseUrl()}${API_PATHS.PROMPT.BASE}`;
    },

    /**
     * Quiz CRUD API base URL
     * Example: https://dev.knowblemind.com/quiz
     */
    get quiz() {
      return `${getBaseUrl()}${API_PATHS.QUIZ.BASE}`;
    },

    /**
     * Subscriptions API base URL
     * Example: https://dev.knowblemind.com/subscriptions
     */
    get subscriptions() {
      return `${getBaseUrl()}${API_PATHS.SUBSCRIPTIONS.BASE}`;
    },

    /**
     * Analytics API base URL
     * Example: https://dev.knowblemind.com/analytics
     */
    get analytics() {
      return `${getBaseUrl()}${API_PATHS.ANALYTICS.BASE}`;
    },

    /**
     * Admin API base URL
     * Example: https://dev.knowblemind.com/admin
     */
    get admin() {
      return `${getBaseUrl()}${API_PATHS.ADMIN.BASE}`;
    },

    /**
     * Articles API base URL
     * Example: https://dev.knowblemind.com/articles
     */
    get articles() {
      return `${getBaseUrl()}${API_PATHS.ARTICLES.BASE}`;
    },
  },

  /**
   * API endpoint paths
   */
  paths: API_PATHS,
} as const;

/**
 * Legacy compatibility - maps old environment variables to new configuration
 * This allows gradual migration from the old URL structure
 */
export const legacyApiConfig = {
  /**
   * AI Prompt Generation API URL (legacy: VITE_API_URL)
   * Now uses: {ENV}.knowblemind.com/prompt
   */
  get apiUrl() {
    return import.meta.env.VITE_API_URL || apiConfig.urls.prompt;
  },

  /**
   * Quiz CRUD API URL (legacy: VITE_CRUD_URL)
   * Now uses: {ENV}.knowblemind.com/quiz
   */
  get crudUrl() {
    return import.meta.env.VITE_CRUD_URL || apiConfig.urls.quiz;
  },

  /**
   * Subscription API URL (legacy: VITE_SUBSCRIPTION_API_URL)
   * Now uses: {ENV}.knowblemind.com/subscriptions
   */
  get subscriptionUrl() {
    return import.meta.env.VITE_SUBSCRIPTION_API_URL || apiConfig.urls.subscriptions;
  },
};

/**
 * Validate API configuration on import
 */
if (import.meta.env.DEV) {
  console.info('[API Config] Environment:', getEnvironment());
  console.info('[API Config] Base URL:', getBaseUrl());
  console.info('[API Config] Prompt API:', apiConfig.urls.prompt);
  console.info('[API Config] Quiz API:', apiConfig.urls.quiz);
  console.info('[API Config] Subscriptions API:', apiConfig.urls.subscriptions);
  console.info('[API Config] Analytics API:', apiConfig.urls.analytics);
  console.info('[API Config] Admin API:', apiConfig.urls.admin);
}
